<template>
    <BaseCard
        as="button"
        padding="sm"
        :elevated="state !== 'complete'"
        :aria-label="label"
        :disabled="isLocked"
        class="flex items-center gap-3 sm:gap-4"
        :class="{
            'opacity-50 cursor-not-allowed': isLocked,
        }"
        @click="handleActionClick">
        <!-- Icon -->
        <div
            class="shrink-0 rounded-full h-5 w-5 flex items-center justify-center"
            :class="{
                'text-ink-muted': state === 'in_progress' && !isLocked,
                'bg-status-error text-ink-inverse': state === 'not_started' && overdue && !isLocked,
                'bg-status-success-soft text-ink': state === 'complete' && !isLocked,
                'bg-surface-sunken text-ink-muted': isLocked,
            }">
            <Checkmark v-if="state === 'complete'" width="10" height="10" />
            <LockSmall v-else-if="isLocked" width="12" height="12" />
            <i v-else-if="icon === 'user'" class="bi bi-person-fill" aria-hidden="true"></i>
            <i v-else-if="icon === 'users'" class="bi bi-people-fill" aria-hidden="true"></i>
            <i v-else-if="icon === 'overdue'" class="bi bi-exclamation-lg" aria-hidden="true"></i>
        </div>

        <!-- Content: title + dateRange stacked on mobile, inline on desktop -->
        <div class="flex-1 min-w-0">
            <div class="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <div class="flex items-center gap-2 min-w-0">
                    <h3 class="body-small-semibold text-ink">
                        {{ state === "complete" ? t("components.moments.complete") : label }}
                    </h3>
                    <div v-if="state === 'complete'" class="body-small-semibold text-ink-muted">{{ label }}</div>
                </div>
                <span v-if="dateRange && state !== 'complete'" class="details text-ink-muted sm:whitespace-nowrap">{{ dateRange }}</span>
            </div>
        </div>

        <!-- Right side - status: stacked on mobile, inline on desktop -->
        <div class="flex items-center gap-2 sm:gap-3 shrink-0">
            <div class="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-4 sm:text-right">
                <div v-if="statusBadge">
                    <span v-if="statusBadge === STATUS_BADGE.VIEW_CHAT" class="body-small text-ink-muted whitespace-nowrap">{{ statusBadgeLabel }}</span>
                    <StatusChip v-else size="2xs" :state="statusBadge === STATUS_BADGE.IN_PROGRESS ? 'active' : 'neutral'">
                        {{ statusBadgeLabel }}
                    </StatusChip>
                </div>
            </div>

            <ArrowRight width="20" height="20" class="shrink-0 text-ink-muted" />
        </div>
    </BaseCard>
</template>

<script setup>
import { useFetch } from "~vue/api";
import BaseCard from "~vue/components/BaseCard.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { formatCtaDateRange } from "~vue/composables/useCtaDateRange";
import ArrowRight from "~vue/icons/ArrowRight.vue";
import Checkmark from "~vue/icons/Checkmark.vue";
import LockSmall from "~vue/icons/LockSmall.vue";
import { openUrl } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
    id: { type: Number, required: true },
    label: { type: String, required: true },
    startDate: { type: String, default: "" },
    dueDate: { type: String, default: "" },
    promptCallCode: { type: String, default: "" },
    existingConvoUrl: { type: String, default: null },
    managerEnabled: { type: Boolean, required: true },
    icEnabled: { type: Boolean, required: true },
    isCompleted: { type: Boolean, required: true },
    isLocked: { type: Boolean, default: false },
    track: { type: Object, default: () => ({}) },
});

const STATUS_BADGE = {
    IN_PROGRESS: "in_progress",
    NOT_STARTED: "not_started",
    VIEW_CHAT: "view_chat",
};
const STATUS_BADGE_LABELS = computed(() => ({
    [STATUS_BADGE.IN_PROGRESS]: t("components.moments.inProgress"),
    [STATUS_BADGE.NOT_STARTED]: t("components.moments.notStarted"),
    [STATUS_BADGE.VIEW_CHAT]: t("components.moments.viewChat"),
}));

const now = DateTime.now();
const dueDate = DateTime.fromFormat(props.dueDate, "yyyy-MM-dd");
const daysUntilDue = Math.ceil(dueDate.diff(now, "days").days);

const dateRange = computed(() => {
    if (props.isCompleted) {
        return "";
    }
    return formatCtaDateRange(props.startDate || null, props.dueDate || null);
});

const state = computed(() => {
    if (props.isCompleted) {
        return "complete";
    }
    if (props.existingConvoUrl) {
        return "in_progress";
    }

    return "not_started";
});

const overdue = computed(() => {
    if (props.isCompleted || !dueDate.isValid) {
        return false;
    }

    return daysUntilDue < 0;
});

const statusBadge = computed(() => {
    switch (state.value) {
        case "in_progress":
            return STATUS_BADGE.IN_PROGRESS;
        case "not_started":
            return STATUS_BADGE.NOT_STARTED;
        case "complete":
            return STATUS_BADGE.VIEW_CHAT;
        default:
            return "";
    }
});
const statusBadgeLabel = computed(() => STATUS_BADGE_LABELS.value[statusBadge.value] ?? "");

const icon = computed(() => {
    if (overdue.value && state.value === "not_started") {
        return "overdue";
    }
    if (props.managerEnabled) {
        return "users";
    }
    return "user";
});

const conversationPayload = ref(null);
const { data: conversationData, execute: startConversation } = useFetch("/api/coach/performance_reviews/conversations/", { immediate: false }).post(conversationPayload).json();

const handleActionClick = async () => {
    // Prevent action on locked CTAs
    if (props.isLocked) {
        return;
    }

    logUserInteraction(props.isCompleted ? "performance_review_card_cta_completed_clicked" : "performance_review_card_cta_clicked", { ...props, ...props.track });

    if (props.existingConvoUrl) {
        openUrl(props.existingConvoUrl);
        return;
    }

    conversationPayload.value = {
        relationship_id: null, // No per-person CTAs in Gen1
        performance_moment_cta_id: props.id,
    };
    await startConversation(true);
    if (conversationData.value?.coaching_session_url) {
        openUrl(conversationData.value.coaching_session_url);
    }
};
</script>
